import { isPlatformBrowser } from '@angular/common'
import { Injectable, InjectionToken, inject, PLATFORM_ID } from '@angular/core'

import { AppConfigService } from 'app/app-config.service'

export const PROMO_TTL_MS = new InjectionToken<number>('PROMO_TTL_MS', {
  providedIn: 'root',
  factory: () => 60 * 60 * 1000 // 1h default
})

@Injectable({ providedIn: 'root' })
export class PromoCodeCacheService {
  private platformId = inject(PLATFORM_ID)
  private ttlMs = inject(PROMO_TTL_MS)
  private appConfig = inject(AppConfigService)

  private get key(): string {
    const id = this.appConfig.getAppConfig?.id ?? 'default'
    return `checkout_promo_${id}`
  }

  load(): string[] {
    if (!isPlatformBrowser(this.platformId)) return []
    try {
      const raw = localStorage.getItem(this.key)
      if (!raw) return []
      const parsed = JSON.parse(raw) as { codes?: string[]; lastModified?: number }
      if (!Array.isArray(parsed?.codes)) return []

      if (parsed.lastModified && Date.now() - parsed.lastModified > this.ttlMs) {
        localStorage.removeItem(this.key)
        return []
      }
      // sanitize, trim, de-dupe
      return [...new Set(parsed.codes.map(c => (c ?? '').trim()).filter(Boolean))]
    } catch {
      return []
    }
  }

  save(codes: string[]): void {
    if (!isPlatformBrowser(this.platformId)) return
    try {
      const payload = { codes, lastModified: Date.now() }
      localStorage.setItem(this.key, JSON.stringify(payload))
    } catch {
      /* ignore */
    }
  }

  clear(): void {
    if (!isPlatformBrowser(this.platformId)) return
    localStorage.removeItem(this.key)
  }
}
